/** Deterministic presence bubble: 2-letter initials + color from a fixed palette. */

export type PresenceAvatarTone = {
  /** Solid fill (Settings / chrome) */
  bg: string
  /** ~50% fill for map pip over the watch hand */
  bgSoft: string
  /** Initials / glyph */
  fg: string
  /** Soft ring / border */
  ring: string
}

/** At least 8 distinct, readable on the dark Studio chrome and on the map. */
export const PRESENCE_AVATAR_TONES: readonly PresenceAvatarTone[] = [
  tone('#2f6f66', '#e8f7f3', '#5ed4c0'),
  tone('#3a5f8a', '#e8f2ff', '#7eb8e8'),
  tone('#6a4f2e', '#fff3e0', '#e8b46a'),
  tone('#5a3f6e', '#f3eaff', '#c4a0e0'),
  tone('#6a3a42', '#ffe8ea', '#e08888'),
  tone('#3f6a48', '#e8f6ea', '#7dcc8a'),
  tone('#4a5a6e', '#e8eef4', '#9ab0c4'),
  tone('#6a5a2e', '#fff8e0', '#d4c060'),
  tone('#2e5a6a', '#e0f4f8', '#6ec4d4'),
  tone('#6a3f5a', '#ffe8f2', '#e0a0c0'),
]

function tone(bg: string, fg: string, ring: string): PresenceAvatarTone {
  return { bg, bgSoft: withAlpha(bg, 0.5), fg, ring }
}

function withAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  if (h.length !== 6) return hex
  const r = Number.parseInt(h.slice(0, 2), 16)
  const g = Number.parseInt(h.slice(2, 4), 16)
  const b = Number.parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function hashKey(key: string): number {
  let h = 2166136261
  const s = key.trim().toLowerCase()
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** Up to two initials from a person name (or first two chars of an email local-part). */
export function presenceInitials(nameOrEmail: string | null | undefined): string {
  const raw = nameOrEmail?.trim() || ''
  if (!raw) return '?'
  if (raw.includes('@')) {
    const local = raw.split('@')[0] ?? raw
    const cleaned = local.replace(/[^a-zA-Z0-9]/g, '')
    return (cleaned.slice(0, 2) || '?').toUpperCase()
  }
  const parts = raw.split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  return parts
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase()
}

/**
 * Stable palette from a short slot (legacy: initials). Prefer {@link presenceToneForKey}
 * with email/userId so people who share initials don’t collide.
 */
export function presenceToneForInitials(initials: string | null | undefined): PresenceAvatarTone {
  const slot = (initials?.trim() || '?').toUpperCase().slice(0, 2) || '?'
  const idx = hashKey(slot) % PRESENCE_AVATAR_TONES.length
  return PRESENCE_AVATAR_TONES[idx]!
}

/** Stable color from a unique person key (email / user id) — not initials. */
export function presenceToneForKey(key: string | null | undefined): PresenceAvatarTone {
  const slot = (key?.trim() || '?').toLowerCase()
  const idx = hashKey(slot) % PRESENCE_AVATAR_TONES.length
  return PRESENCE_AVATAR_TONES[idx]!
}
